// Frontend feature flag for the AI Text Agent surface.
//
// Mirrors the aiDialerFlag pattern. The flag is effectively true when EITHER:
//   • VITE_ENABLE_AI_TEXT_AGENT === 'true' (build-time env), OR
//   • the auth'd tenant has features.aiTextAgentEnabled === true
//     (runtime — fetched from /api/tenant/features after login)
//
// Scaffolded 2026-07-06 alongside sidebar entry. Route `/ai-text-agent`
// wired in PR #385. Backend serves features.aiTextAgentEnabled from
// /tenant/features (present since PR #385). Toggle per-tenant via
// POST /api/admin/tenants/:tenantId/features/ai-text-agent (PR #387).
// The sidebar link stays hidden until the flag is true.

import { useEffect, useState } from 'react';
import { apiGet } from './api.js';

export const AI_TEXT_AGENT_BUILD_FLAG = import.meta.env.VITE_ENABLE_AI_TEXT_AGENT === 'true';

let _runtimeFlag = null;
let _inflight = null;

export async function fetchAiTextAgentFlag() {
  if (_runtimeFlag !== null) return _runtimeFlag;
  if (_inflight) return _inflight;
  _inflight = apiGet('/tenant/features')
    .then((r) => {
      _runtimeFlag = !!r?.features?.aiTextAgentEnabled;
      return _runtimeFlag;
    })
    .catch(() => {
      _runtimeFlag = false;
      return false;
    })
    .finally(() => { _inflight = null; });
  return _inflight;
}

export function clearAiTextAgentCache() { _runtimeFlag = null; }

export function isAITextAgentEnabled() {
  return AI_TEXT_AGENT_BUILD_FLAG || _runtimeFlag === true;
}

// React hook — returns { enabled, loading }. Triggers the fetch on first use
// and shares the cache across components. Mirrors useAIDialerEnabled.
export function useAITextAgentEnabled() {
  const [enabled, setEnabled] = useState(AI_TEXT_AGENT_BUILD_FLAG || _runtimeFlag === true);
  const [loading, setLoading] = useState(_runtimeFlag === null && !AI_TEXT_AGENT_BUILD_FLAG);
  useEffect(() => {
    if (AI_TEXT_AGENT_BUILD_FLAG) return;
    if (_runtimeFlag !== null) {
      setEnabled(_runtimeFlag);
      setLoading(false);
      return;
    }
    let alive = true;
    fetchAiTextAgentFlag().then((flag) => {
      if (!alive) return;
      setEnabled(!!flag);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);
  return { enabled, loading };
}
